import {
  LitElement,
  html,
  css,
  t,
  classMap,
  paths,
  unsafeHTML,
} from '@hh/global.js'

class HHBottleEngravingPersonalization extends LitElement {
  static properties = {
    type: {
      type: String,
    },
    defaultStyle: {
      type: String,
      attribute: 'default-style',
    },
    defaultColor: {
      type: String,
      attribute: 'default-color',
    },
    engravingColor: {
      type: String,
      attribute: 'engraving-color',
    },
    stylesCount: {
      type: Number,
      attribute: 'styles-count',
    },
    currentStyle: {
      type: String,
    },
    currentColor: {
      type: String,
    },
    currentValueLine1: {
      type: String,
    },
    currentValueLine2: {
      type: String,
    },
    currentVariant: {
      type: Object
    },
    currentVariantTitle: {
      type: String,
      attribute: 'current-variant-title',
    },
    maxCharsDefaultLine1: {
      type: Number,
      attribute: 'max-chars-line-1',
    },
    maxCharsDefaultLine2: {
      type: Number,
      attribute: 'max-chars-line-2',
    },
    maxChars100mlLine1: {
      type: Number,
      attribute: 'max-chars-100ml-line-1',
    },
    maxChars100mlLine2: {
      type: Number,
      attribute: 'max-chars-100ml-line-2',
    },
    line1Error: {
      type: Boolean,
    },
    line2Error: {
      type: Boolean,
    },
    requireTerms: {
      type: Boolean,
      attribute: 'require-terms',
    },
    agreeToTerms: {
      type: Boolean,
    },
    agreeToTermsError: {
      type: Boolean,
    },
    _trapFocus: {
      state: true,
    },
    showBackButton: {
      type: Boolean,
      attribute: 'show-back-button'
    },
    textInputs: {
      type: Number,
      state: true,
    }
  }


  constructor() {
    super()

    this.classes = {
      active: 'active',
    }

    this.allowedChars = /^[A-Za-z0-9\s!@#$%^&*()_+\-=[\]{}|;:'"“”‘’„‚.,?~`Ñ$€£¥₹/]*$/
    this.disallowedChars = /[^A-Za-z0-9\s!@#$%^&*()_+\-=[\]{}|;:'"“”‘’„‚.,?~`Ñ$€£¥₹/]/g

    this.currentValueLine1 = ''
    this.currentValueLine2 = ''
    this.agreeToTerms = false
  }

  get personalizeComponent() {
    return document.querySelector('hh-personalize') ?? null
  }

  get fontStyleButtons() {
    const slot = this.shadowRoot.querySelector('slot[name="font-style"]')
    return slot?.assignedElements() ?? []
  }

  get colorButtons() {
    const slot = this.shadowRoot.querySelector('slot[name="color-option"]')
    return slot?.assignedElements() ?? []
  }

  get addButton() {
    const slot = this.shadowRoot.querySelector('slot[name="add"]')
    return slot?.assignedElements()[0] ?? null
  }

  get submitButton() {
    const slot = this.shadowRoot.querySelector('slot[name="submit"]')
    return slot?.assignedElements()[0] ?? null
  }

  get disableATC() {
    const textLines = Array(this.textInputs).fill(0).map((_, index) => this[`currentValueLine${index + 1}`])

    return textLines.some(line => (line.match(/[A-Z]/g) || []).length > 4) || !this.agreeToTerms
  }

  connectedCallback() {
    super.connectedCallback()
    this.currentStyle = this.defaultStyle

    window.addEventListener(
      'drawer:open',
      this.onPersonalizeDrawerOpen.bind(this),
    )

    this.resetInitialization()

    window.addEventListener('variant:change', this.onVariantChange.bind(this))

    window.addEventListener(
      'personalization:reset',
      this.resetPersonalization.bind(this)
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('personalization:reset')
    window.removeEventListener('drawer:open', this.onPersonalizeDrawerOpen)
  }

  get personalizeComponent() {
    return document.querySelector('hh-personalize') ?? null
  }

  get personalizationMetafields() {
    const metafieldsStriptTag = document.getElementById(
      'personalizationMetafields',
    )
    const json = metafieldsStriptTag
      ? JSON.parse(metafieldsStriptTag.textContent)
      : null
    return json
  }

  get variantPersonalizationMetafields() {
    return this.personalizationMetafields.find(
      (variant) => variant.id === this.currentVariant.id,
    )
  }

  resetPersonalization() {
    this.clearPersonalization()
  }

  updated(changedProperties) {
    if (changedProperties.has('currentStyle')) {
      this.updateStyle(this.currentStyle)
    }
    if (changedProperties.has('currentColor')) {
      this.updateColor(this.currentColor)
    }

    (this.addButton || this.submitButton).disabled = this.disableATC
  }

  onPersonalizeDrawerOpen(e) {
    const { target } = e
    if (!target?.classList.contains('js-personalize-drawer')) return
    this.currentVariant = this.personalizeComponent?.currentVariant

    const variantTitle = this.getVariantTitle(this.currentVariant)
    if (variantTitle) {
      this.currentVariantTitle = variantTitle.toLowerCase()
      this.resetInitialization()
    }

    if (this.type === 'bottle') {
      this.maxCharsLine1 = this.variantPersonalizationMetafields.bottleEngravingMaxCharsLine1
      this.maxCharsLine2 = this.variantPersonalizationMetafields.bottleEngravingMaxCharsLine2
    }

    this.updateStyle(this.currentStyle)
  }

  onStyleChange(e) {
    const style = e.target.value

    if (!style) return
    this.currentStyle = style
  }

  updateStyle(style) {
    this.dispatchEvent(
      new CustomEvent('personalize:update:style', {
        bubbles: true,
        detail: {
          type: this.type,
          style,
        },
      })
    )
  }

  onColorChange(e) {
    const color = e.target.value
    if (!color) return
    this.currentColor = color
  }

  updateColor(color) {
    this.dispatchEvent(
      new CustomEvent('personalize:update:color', {
        bubbles: true,
        detail: {
          color,
        },
      })
    )
  }

  styleOptionsTemplate() {
    if (this.stylesCount <= 1) return null
    return html`
      <fieldset class="styles">
        <legend class="options-label styles-label">
          ${t('products.product.personalization.select_style')}
        </legend>
        <div class="style-options">
          <slot name="font-style" @change=${this.onStyleChange}></slot>
        </div>
      </fieldset>
    `
  }

  colorOptionsTemplate() {
    if (!this.hasColorOptions) return null
    return html`
      <fieldset class="colors">
        <legend class="options-label colors-label">
          ${t('products.product.personalization.select_color')}
        </legend>
        <div class="color-options">
          <slot name="color-option" @change=${this.onColorChange}></slot>
        </div>
      </fieldset>
    `
  }

  onLineInput(e, line) {
    const { target } = e
    let inputValue = target.value
    inputValue = inputValue.replace(this.disallowedChars, '')

    if (this.type === 'embossing') {
      if (inputValue.length >= this.maxCharsDefaultLine1) {
        this.maxCharsLine1 = this.variantPersonalizationMetafields.verticalLeatherEmbossingMaxChars
      } else {
        this.maxCharsLine1 = this.maxCharsDefaultLine1
      }
    }

    if (inputValue.length >= this[`maxCharsLine${line}`]) {
      inputValue = inputValue.slice(0, this[`maxCharsLine${line}`])
    }

    this.updateLine(line, inputValue)
  }

  updateLine(line, value) {
    this[`currentValueLine${line}`] = value
    this[`line${line}Error`] = (value.match(/[A-Z]/g) || []).length > 4

    this.dispatchEvent(
      new CustomEvent('personalize:update:line', {
        bubbles: true,
        detail: {
          type: this.type,
          line,
          value,
        },
      })
    )
  }

  onLineKeydown(e, line) {
    const { key } = e
    if (!key.match(this.allowedChars)) {
      e.preventDefault()
      return
    }

    const allowedKeys = ['Backspace', 'ArrowLeft', 'ArrowRight', 'Tab']
    if (
      this[`currentValueLine${line}`]?.length >= this[`maxCharsLine${line}`] &&
      !allowedKeys.includes(key)
    ) {
      e.preventDefault()
      return
    }
  }

  onCheckboxChange(e) {
    const { checked } = e.target
    this.agreeToTerms = checked
    this.agreeToTermsError = false
  }

  onCancelClick(e) {
    e.preventDefault()
    // this.clearPersonalization()
  }

  clearPersonalization() {
    this.updateLine(1, '')
    this.updateLine(2, '')
    this.agreeToTerms = false
    this.currentStyle = this.defaultStyle
    this.currentColor = this.defaultColor

    this.fontStyleButtons.forEach((el, i) => {
      const radio = el.querySelector('input[type="radio"]')
      if (radio) radio.checked = i === 0
    })

    this.colorButtons.forEach((el, i) => {
      const radio = el.querySelector('input[type="radio"]')
      if (radio) radio.checked = i === 0
    })
  }

  onAddClick(e) {
    // if (this.addButton.disabled) return

    if (this.type === 'bottle') {
      if (this.validatePersonalization(2) === false) return

      this.personalizeComponent?.addBottleEngravingOption({
        message1: this.currentValueLine1,
        message2: this.currentValueLine2,
        style: HH.capitalize(this.currentStyle),
      })
    } else if (this.type === 'medallion') {
      if (this.validatePersonalization(1) === false) return

      this.personalizeComponent?.addMedallionEngravingOption({
        initials: this.currentValueLine1,
        style: HH.capitalize(this.currentStyle),
        color: this.currentColor,
      })
    } else if (this.type === 'embossing') {
      if (this.validatePersonalization(1) === false) return
      this.personalizeComponent.addLeatherEmbossingOption({
        initials: this.currentValueLine1,
        style: HH.capitalize(this.currentStyle),
      })
    }

    if (e === 'submit') {
      this.dispatchEvent(
        new CustomEvent('submit:engraving', { bubbles: true })
      )

      this.dispatchEvent(
        new CustomEvent('drawer:close', { bubbles: true })
      )
    }

    this.dispatchEvent(
      new CustomEvent('double-drawer:close', { bubbles: true })
    )

    this.dispatchEvent(
      new CustomEvent('personalization:confirm', { bubbles: true })
    )
  }

  validatePersonalization(numberOfLines) {
    let isValid = true
    for (let i = 1; i <= numberOfLines; i++) {
      const value = this[`currentValueLine${i}`]
      if (value === '' && i === 1) {
        this[`line${i}Error`] = true
        isValid = false
      }
    }

    if (!this.agreeToTerms) {
      this.agreeToTermsError = true
      isValid = false
    }

    return isValid
  }

  isSingleLineBottleVariant(variantTitle = '') {
    return /\b30\s*ml\b/i.test(variantTitle)
  }

  getVariantTitle(variant = {}) {
    if (typeof variant?.title === 'string' && variant.title.trim()) {
      return variant.title.trim()
    }

    if (typeof variant?.option1 === 'string' && variant.option1.trim()) {
      return variant.option1.trim()
    }

    return ''
  }

  resetInitialization() {
    if (this.type === 'bottle') {
      this.textInputs = this.isSingleLineBottleVariant(this.currentVariantTitle) ? 1 : 2
      this.textLinesLabel = t(
        'products.product.personalization.add_your_message'
      )
      this.hasColorOptions = false
    } else if (this.type === 'medallion') {
      this.textInputs = 1
      this.hasColorOptions = true
      this.textLinesLabel = t('products.product.personalization.initials')
      this.currentColor = this.defaultColor
    } else if (this.type === 'embossing') {
      this.textInputs = 1
      this.textLinesLabel = t('products.product.personalization.initials')
      this.allowedChars = /^[a-zA-Z\s]*$/
      this.disallowedChars = /[^a-zA-Z\s]/g
    }

    this.maxCharsLine1 = this.maxCharsDefaultLine1
    this.maxCharsLine2 = this.maxCharsDefaultLine2
  }

  onVariantChange(e) {
    const { detail } = e
    this.currentVariantTitle = this.getVariantTitle(detail.variant).toLowerCase()
    this.resetInitialization()
    this.resetPersonalization()
  }

  textLinesTemplate() {
    return html`
      <div class="messages">
        <div class="options-label messages-label">${this.textLinesLabel}</div>
        <div class="text-lines">
          ${Array(this.textInputs).fill(0).map((_, index) => this.lineInputTemplate(
      this[`currentValueLine${index + 1}`],
      `${index + 1}`,
      this[`maxCharsLine${index + 1}`]
    ))
      }
        </div>
      </div>
    `
  }

  lineInputTemplate(currentValue, line, maxChars) {
    return html`
      <div
        class=${classMap({
      'has-error': this[`line${line}Error`],
    })}
      >
        <div
          class=${classMap({
      'line-input-wrapper': true,
      'line-input-wrapper--active': currentValue !== '',
    })}
        >
          <label class="line-input-label" for="bottleEngravingLine${line}">
            ${t(`products.product.personalization.line_label_${line}`)}
          </label>
          <input
            type="text"
            name="bottle_engraving_line_${line}"
            id="bottleEngravingLine${line}"
            class="line-input"
            @input=${(e) => this.onLineInput(e, line)}
            @keydown=${(e) => this.onLineKeydown(e, line)}
            .value=${currentValue}
          />
          <span class="line-char-count">
            (${currentValue?.length ?? 0}/${maxChars})
          </span>
          <span class="line-char-width">${currentValue}</span>
        </div>
        ${this.errorTemplate(
      currentValue?.length ? t('products.product.personalization.max_caps_reached') : t('products.product.personalization.required_field')
    )}
      </div>
    `
  }

  termsTemplate() {
    if (!this.requireTerms) return null
    return html`
      <div
        class=${classMap({
      'terms-wrapper': true,
      'has-error': this.agreeToTermsError,
    })}
      >
        <label class="terms">
          <input
            type="checkbox"
            name="terms"
            .checked=${this.agreeToTerms}
            @change=${this.onCheckboxChange}
          />
          <span class="checkmark"></span>
          <slot name="terms-content"></slot>
        </label>
        ${this.errorTemplate(
      t('products.product.personalization.required_terms')
    )}
      </div>
    `
  }

  errorTemplate(message) {
    return html`
      <div class="error" role="alert">
        <div class="error-icon">${unsafeHTML(paths.error)}</div>
        <div class="error-message">${message}</div>
      </div>
    `
  }

  render() {
    return html`
      <div class="container">
        ${this.showBackButton ? html`
          <div class="back">
            <slot name="back"></slot>
          </div>
        ` : null}
        <div class="inner">
          <slot name="heading"></slot>
          <slot name="preview"></slot>
          ${this.textLinesTemplate()}

          ${this.styleOptionsTemplate()}
          ${this.colorOptionsTemplate()}

          ${this.termsTemplate()}
        </div>
        <div class="footer">
          <slot name="cancel" @click=${this.onCancelClick}></slot>
          <slot name="add" @click=${() => this.onAddClick('add')}></slot>
          <slot name="submit" @click=${() => this.onAddClick('submit')}></slot>
        </div>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .container {
      display: flex;
      flex-direction: column;
      height: 100dvh;
      width: 100%;
    }

    .back {
      padding: 1.3750rem 1.2500rem 0;
    }

    @media (min-width: 1024px) {
      .back {
        padding: 3.4375rem 2.5000rem 0;
      }
    }

    .inner {
      padding: 64px 16px;
      flex: 1 0 auto;
      overflow-y: auto;
      max-height: calc(100dvh - 85px);
    }

    @media (min-width: 1024px) {
      .inner {
        padding: 64px 40px;
        height: 0;
      }
    }

    .options-label {
      font-size: 12px;
      letter-spacing: 0.2px;
      line-height: 150%;
      font-weight: 500;
      margin-bottom: 0.5rem;
    }

    @media (min-width: 1024px) {
      .options-label {
        font-size: 14px;
      }
    }

    .text-lines {
      display: flex;
      flex-direction: column;
      gap: 16px;
    }

    .style-options,
    .color-options {
      display: flex;
      gap: 0.6250rem;
      flex-wrap: wrap;
    }

    .messages,
    .colors,
    .styles {
      margin: 24px 0 0;
      border: 0;
      padding: 0;
    }

    .line-input-wrapper {
      position: relative;
    }

    .line-input-label {
      position: absolute;
      color: #595959;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%; /* 21px */
      letter-spacing: 0.2px;
      left: 14px;
      top: 16px;
      transition: all var(--hh-duration-default) ease-in-out;
    }

    .line-input-wrapper--active .line-input-label,
    .line-input-wrapper:focus-within .line-input-label {
      top: 0.1250rem;
      transform: translateY(0);
      font-size: 0.7500rem;
    }

    .line-input {
      width: 100%;
      height: 100%;
      border: 1px solid #666;
      border-radius: 0;
      padding: 16px 14px;
      font-size: 16px;
      letter-spacing: 0.2px;
      transition: all var(--hh-duration-default);
      height: 53px;
    }

    .has-error .line-input {
      border-color: var(--hh-color-danger);
    }

    .line-input-wrapper:hover .line-input {
      box-shadow: 0px 0px 20px var(--hh-color-night-xx-light-opacity);
    }

    .line-input:focus-visible {
      outline: none;
      border-color: var(--hh-color-night);
    }

    .line-input:focus {
      top: 0;
    }

    .line-char-count {
      position: absolute;
      right: 14px;
      top: 16px;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 150%;
      letter-spacing: 0.2px;
    }

    .line-char-width {
      display: inline;
      visibility: hidden;
    }

    .error {
      margin: 0.5000rem 0 0;
      font: var(--hh-body-x-small-font);
      color: var(--hh-color-danger);
      align-items: center;
      gap: 0.3125rem;
      display: none;
    }

    .has-error .error {
      display: flex;
    }

    .error-icon {
      display: flex;
      align-items: center;
    }

    .terms-wrapper {
      margin-top: 24px;
    }

    .terms {
      display: flex;
      align-items: flex-start;
      gap: 0.6250rem;
      cursor: pointer;
    }

    .terms input {
      position: absolute;
      opacity: 0;
      cursor: pointer;
      height: 0;
      width: 0;
    }

    .checkmark {
      width: 1.0000rem;
      height: 1.0000rem;
      border: 1px solid var(--hh-color-neutral);
      flex: 1 0 auto;
      position: relative;
      flex-grow: 0;
      margin-top: 6px;
    }

    .terms-wrapper:hover .checkmark {
      border-color: var(--hh-color-night);
    }

    .checkmark::after {
      content: ' ';
      position: absolute;
      display: none;
      left: 5px;
      top: 1px;
      width: 4px;
      height: 9px;
      border: 1px solid var(--hh-color-night);
      border-width: 0 1px 1px 0;
      -webkit-transform: rotate(45deg);
      -ms-transform: rotate(45deg);
      transform: rotate(45deg) scale(1.2);
    }

    .terms input:focus-visible ~ .checkmark {
      outline: 0.1250rem solid var(--hh-color-focus);
      outline-offset: var(--hh-focus-outline-offset);
    }

    .terms input:checked ~ .checkmark {
      border-color: var(--hh-color-night);
    }

    .terms input:checked ~ .checkmark::after {
      display: block;
    }

    .footer {
      display: flex;
      margin-top: -1px;
      position: fixed;
      bottom: 0;
      width: 100%;
      left: 0;
      gap: 1.0000rem;
      padding: 1.0000rem;
      background: var(--hh-color-day);
    }
  `
}

customElements.define('hh-bottle-engraving-personalization', HHBottleEngravingPersonalization)
